/**
 * Bộ điều khiển chuỗi (core-loop 5.1): gộp kéo (drag) và chạm (tap) vào một máy trạng thái.
 * Thuần (không DOM/pixi) để test được. Luật nối lấy hết từ @ml/core.
 *
 * Quyết định E1 (review s1): "chạm viên cuối → gỡ" xung đột "chạm đúp viên cuối → xác nhận".
 * Chốt: chạm viên cuối luôn gỡ viên đó; chỉ xác nhận bằng nút Cast (bỏ chạm đúp, kể cả chạm
 * đúp booster). Không cần cửa sổ double-tap nên không có độ trễ và không thêm key config.
 *
 * Phân biệt kéo / chạm trong cùng một cử chỉ pointerdown → pointerup:
 * - Cử chỉ làm chuỗi đổi khi di chuyển (nối thêm / quay lui) là KÉO → pointerup thả chuỗi.
 * - Không đổi gì khi di chuyển là CHẠM → chuỗi giữ nguyên chờ chạm tiếp hoặc Cast.
 * Nhờ vậy có thể chạm vài viên rồi kéo tiếp từ viên cuối; thả tay là thả chuỗi.
 */
import {
  canStartAt,
  dragEnter,
  getLinkCandidates,
  isCompleteMove,
  NO_PATTERNS,
  sameCoord,
  type Board,
  type Coord,
  type LinkContext,
} from '@ml/core';
import type { Config } from '@ml/config';

export type ChainConfig = Pick<Config, 'link' | 'essence'>;

export type ChainOutcome =
  | { readonly type: 'none' }
  | { readonly type: 'changed' }
  | { readonly type: 'cancel' }
  | { readonly type: 'release'; readonly path: readonly Coord[] };

type Gesture = { moved: boolean; tapOnLast: boolean };

export class ChainController {
  private _path: readonly Coord[] = [];
  private _candidates: Coord[] = [];
  private gesture: Gesture | null = null;

  constructor(
    private board: Board,
    private readonly config: ChainConfig,
    private ctx: LinkContext = NO_PATTERNS,
  ) {
    this.refresh();
  }

  get path(): readonly Coord[] {
    return this._path;
  }
  /** Ứng viên của chuỗi hiện tại (chuỗi rỗng: mọi viên bắt đầu được). */
  get candidates(): readonly Coord[] {
    return this._candidates;
  }
  get gestureActive(): boolean {
    return this.gesture !== null;
  }
  /** Chuỗi hiện tại là một nước hoàn chỉnh → hiện nút Cast. */
  get castable(): boolean {
    return this._path.length > 0 && isCompleteMove(this.board, this._path, this.config, this.ctx);
  }

  /** Bảng mới (và ngữ cảnh lọc tiền tố mới, nếu có: skill hồi chiêu / ult đầy đổi theo lượt). */
  setBoard(board: Board, ctx?: LinkContext): void {
    this.board = board;
    if (ctx) this.ctx = ctx;
    this._path = [];
    this.gesture = null;
    this.refresh();
  }

  /** pointerdown; `c = null` khi chạm ngoài lưới (huỷ chuỗi đang chọn). */
  down(c: Coord | null): ChainOutcome {
    if (c === null) {
      this.gesture = null;
      return this.clear();
    }
    const p = this._path;
    if (p.length === 0) {
      if (!canStartAt(this.board, c, this.config)) return { type: 'none' };
      this.gesture = { moved: false, tapOnLast: false };
      return this.set([c]);
    }
    if (sameCoord(p[p.length - 1]!, c)) {
      this.gesture = { moved: false, tapOnLast: true };
      return { type: 'none' };
    }
    if (this._candidates.some((k) => sameCoord(k, c))) {
      this.gesture = { moved: false, tapOnLast: false };
      return this.set([...p, c]);
    }
    if (p.some((k) => sameCoord(k, c))) {
      // Viên giữa chuỗi: bỏ qua (không bắt đầu lại, không gỡ).
      this.gesture = null;
      return { type: 'none' };
    }
    if (canStartAt(this.board, c, this.config)) {
      this.gesture = { moved: false, tapOnLast: false };
      return this.set([c]);
    }
    this.gesture = null;
    return { type: 'none' };
  }

  /** Ngón tay đi vào vùng chạm của ô `c` trong lúc giữ. */
  enter(c: Coord): ChainOutcome {
    if (!this.gesture) return { type: 'none' };
    const next = dragEnter(this.board, this._path, c, this.config, this.ctx);
    if (next === this._path) return { type: 'none' };
    this.gesture.moved = true;
    return this.set(next);
  }

  up(): ChainOutcome {
    const g = this.gesture;
    this.gesture = null;
    if (!g) return { type: 'none' };
    if (g.moved) return this.release();
    if (g.tapOnLast) {
      const next = this._path.slice(0, -1);
      return next.length === 0 ? this.clear() : this.set(next);
    }
    return { type: 'none' };
  }

  /** pointercancel: huỷ chuỗi. */
  cancel(): ChainOutcome {
    this.gesture = null;
    return this.clear();
  }

  /** Nút Cast. */
  cast(): ChainOutcome {
    if (!this.castable) return { type: 'none' };
    this.gesture = null;
    return this.release();
  }

  private release(): ChainOutcome {
    const path = this._path;
    this._path = [];
    this.refresh();
    return path.length === 0 ? { type: 'none' } : { type: 'release', path };
  }

  private clear(): ChainOutcome {
    if (this._path.length === 0) return { type: 'none' };
    this._path = [];
    this.refresh();
    return { type: 'cancel' };
  }

  private set(path: readonly Coord[]): ChainOutcome {
    this._path = path;
    this.refresh();
    return { type: 'changed' };
  }

  private refresh(): void {
    this._candidates = getLinkCandidates(this.board, this._path, this.config, this.ctx);
  }
}
